import React, { useState } from 'react';
import { Download, Send, Plus, CheckCircle2, FileText, Printer } from 'lucide-react';

export function Proposal({ showToast }) {
  const [selectedProposalId, setSelectedProposalId] = useState('prop-101');
  const [isGenerating, setIsGenerating] = useState(false);

  // Real Business Proposals & Quotations List
  const proposalList = [
    {
      id: 'prop-101',
      code: 'QT-2026-0801',
      title: 'Commercial Office Space & Marina Berth Lease',
      customerName: 'Khun Somchai (Phuket Marine Tech Co., Ltd.)',
      customerPhone: '081-998-2234',
      date: '25 Aug 2026',
      status: 'Pending Approval',
      statusColor: '#D97706',
      items: [
        { desc: 'Commercial Office & Retail Lease (150 sq.m.)', qty: 1, unitPrice: 75000, amount: 75000 },
        { desc: 'Marina Berth Mooring Lease 40ft', qty: 1, unitPrice: 35000, amount: 35000 },
        { desc: 'Corporate Client Discount', qty: 1, unitPrice: -10000, amount: -10000 }
      ],
      subtotal: 100000,
      vat: 7000,
      grandTotal: 107000,
      notes: 'Handover within 15 business days. 3-month security deposit required.'
    },
    {
      id: 'prop-102',
      code: 'QT-2026-0802',
      title: 'Tower Office Space Lease (14th Floor)',
      customerName: 'Trident Law Partners',
      customerPhone: '02-123-4567',
      date: '24 Aug 2026',
      status: 'Approved',
      statusColor: '#059669',
      items: [
        { desc: 'Office Lease 120 sq.m. (14th Floor)', qty: 1, unitPrice: 65000, amount: 65000 },
        { desc: 'Common Area Fee & Central Air-Conditioning', qty: 1, unitPrice: 10000, amount: 10000 },
        { desc: 'Reserved Parking Privileges (2 slots)', qty: 2, unitPrice: 0, amount: 0 }
      ],
      subtotal: 75000,
      vat: 5250,
      grandTotal: 80250,
      notes: 'Minimum 2-year lease agreement. 3-month security deposit.'
    },
    {
      id: 'prop-103',
      code: 'QT-2026-0803',
      title: 'Luxury Waterfront Villa Investment Proposal',
      customerName: 'Khun Anan Phumirat',
      customerPhone: '089-445-1123',
      date: '22 Aug 2026',
      status: 'Sent',
      statusColor: '#2563EB',
      items: [
        { desc: 'Luxury Waterfront Villa (3 Bedrooms)', qty: 2, unitPrice: 7500000, amount: 15000000 },
        { desc: 'Full Furnishing & Interior Decor Package', qty: 2, unitPrice: 500000, amount: 1000000 },
        { desc: 'Special Pre-Opening Discount', qty: 1, unitPrice: -500000, amount: -500000 }
      ],
      subtotal: 15500000,
      vat: 0,
      grandTotal: 15500000,
      notes: 'Guaranteed rental yield of 7% per annum for 3 years.'
    }
  ];

  const activeProp = proposalList.find(p => p.id === selectedProposalId) || proposalList[0];

  const handleDownloadPdf = () => {
    setIsGenerating(true);
    setTimeout(() => {
      setIsGenerating(false);
      if (showToast) showToast('Quotation PDF downloaded successfully', 'success');
      alert(`Quotation PDF for code ${activeProp.code} downloaded successfully.`);
    }, 600);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Header & Controls */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h2 style={{ fontSize: '18px', fontWeight: 700, color: '#09090B' }}>Proposals & Quotations</h2>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <button
            type="button"
            onClick={handleDownloadPdf}
            style={{
              height: '36px',
              padding: '0 14px',
              borderRadius: '6px',
              border: 'none',
              backgroundColor: '#10B981',
              color: '#FFFFFF',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <Download size={14} />
            <span>{isGenerating ? 'Generating PDF...' : 'Download PDF'}</span>
          </button>
        </div>
      </div>

      {/* Main Layout: Left Selector & Right Clean Quotation Document */}
      <div className="chatbot-responsive-container">
        {/* Left Column: Proposals List */}
        <div className="card" style={{ padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '14px 18px', backgroundColor: '#FAFAFC', borderBottom: '1px solid #E4E4E7', fontSize: '13px', fontWeight: 700, color: '#09090B' }}>
            Recent Quotation Proposals
          </div>

          <div style={{ flex: 1 }}>
            {proposalList.map((p) => {
              const isSelected = p.id === selectedProposalId;
              return (
                <div
                  key={p.id}
                  onClick={() => setSelectedProposalId(p.id)}
                  style={{
                    padding: '14px 18px',
                    borderBottom: '1px solid #F4F4F5',
                    backgroundColor: isSelected ? '#ECFDF5' : '#FFFFFF',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <span style={{ fontSize: '12px', fontWeight: 700, color: '#10B981', fontFamily: 'var(--font-mono)' }}>{p.code}</span>
                    <span style={{ fontSize: '11px', color: '#71717A' }}>{p.date}</span>
                  </div>

                  <div style={{ fontSize: '13.5px', fontWeight: 700, color: '#09090B', marginBottom: '2px' }}>
                    {p.customerName}
                  </div>

                  <div style={{ fontSize: '12px', color: '#52525B', marginBottom: '6px', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                    {p.title}
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '12px', fontWeight: 700, color: '#09090B', fontFamily: 'var(--font-mono)' }}>
                      ฿{p.grandTotal.toLocaleString()}
                    </span>
                    <span style={{ fontSize: '10.5px', fontWeight: 600, color: p.statusColor }}>
                      {p.status}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Right Column: Clean Standard Quotation Preview */}
        <div className="card" style={{ padding: '32px', backgroundColor: '#FFFFFF', display: 'flex', flexDirection: 'column', gap: '24px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
          {/* Header Banner */}
          <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '2px solid #090D16', paddingBottom: '16px' }}>
            <div>
              <div style={{ fontSize: '18px', fontWeight: 800, color: '#09090B' }}>TBS MARKETING CRM</div>
              <div style={{ fontSize: '12px', color: '#71717A', marginTop: '2px' }}>RPM Tower, Bangkok Metro, Tel: 02-998-8888</div>
            </div>

            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '20px', fontWeight: 800, color: '#10B981' }}>QUOTATION</div>
              <div style={{ fontSize: '12.5px', fontWeight: 600, color: '#09090B', marginTop: '2px', fontFamily: 'var(--font-mono)' }}>
                No: {activeProp.code}
              </div>
              <div style={{ fontSize: '12px', color: '#71717A' }}>Date: {activeProp.date}</div>
            </div>
          </div>

          {/* Customer Info Box */}
          <div style={{ padding: '16px', backgroundColor: '#FAFAFC', border: '1px solid #E4E4E7', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
            <div>
              <div style={{ fontSize: '11.5px', color: '#71717A', fontWeight: 500 }}>Customer Details</div>
              <div style={{ fontSize: '15px', fontWeight: 700, color: '#09090B', marginTop: '2px' }}>{activeProp.customerName}</div>
              <div style={{ fontSize: '12.5px', color: '#52525B', marginTop: '1px' }}>Phone: {activeProp.customerPhone}</div>
            </div>

            <div>
              <div style={{ fontSize: '11.5px', color: '#71717A', fontWeight: 500 }}>Project Title / Description</div>
              <div style={{ fontSize: '14px', fontWeight: 700, color: '#09090B', marginTop: '2px' }}>{activeProp.title}</div>
              <span style={{ fontSize: '11.5px', fontWeight: 600, color: activeProp.statusColor, display: 'inline-block', marginTop: '4px' }}>
                Status: {activeProp.status}
              </span>
            </div>
          </div>

          {/* Items Table */}
          <div className="table-responsive">
            <table className="custom-table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th style={{ width: '40px' }}>#</th>
                  <th>Item / Service Description</th>
                  <th style={{ textAlign: 'center', width: '80px' }}>Qty</th>
                  <th style={{ textAlign: 'right', width: '120px' }}>Unit Price</th>
                  <th style={{ textAlign: 'right', width: '130px' }}>Amount (THB)</th>
                </tr>
              </thead>
              <tbody>
                {activeProp.items.map((item, idx) => (
                  <tr key={idx}>
                    <td style={{ color: '#71717A', fontSize: '12px' }}>{idx + 1}</td>
                    <td style={{ fontWeight: 600, color: '#09090B' }}>{item.desc}</td>
                    <td style={{ textAlign: 'center', fontWeight: 500 }}>{item.qty}</td>
                    <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)' }}>
                      {item.unitPrice < 0 ? `-฿${Math.abs(item.unitPrice).toLocaleString()}` : `฿${item.unitPrice.toLocaleString()}`}
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: 700, color: item.amount < 0 ? '#DC2626' : '#09090B', fontFamily: 'var(--font-mono)' }}>
                      {item.amount < 0 ? `-฿${Math.abs(item.amount).toLocaleString()}` : `฿${item.amount.toLocaleString()}`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Calculation Summary Footer */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px', paddingTop: '12px', borderTop: '1px solid #E4E4E7' }}>
            <div style={{ maxWidth: '350px' }}>
              <div style={{ fontSize: '12px', fontWeight: 600, color: '#09090B' }}>Terms & Notes:</div>
              <div style={{ fontSize: '12px', color: '#52525B', marginTop: '4px', lineHeight: 1.5 }}>
                {activeProp.notes}
              </div>
            </div>

            <div style={{ width: '260px', display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '13px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', color: '#52525B' }}>
                <span>Subtotal:</span>
                <span style={{ fontWeight: 600, color: '#09090B', fontFamily: 'var(--font-mono)' }}>฿{activeProp.subtotal.toLocaleString()}</span>
              </div>
              {activeProp.vat > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', color: '#52525B' }}>
                  <span>VAT 7%:</span>
                  <span style={{ fontWeight: 600, color: '#09090B', fontFamily: 'var(--font-mono)' }}>฿{activeProp.vat.toLocaleString()}</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 12px', backgroundColor: '#ECFDF5', border: '1px solid #A7F3D0', borderRadius: '6px', marginTop: '4px' }}>
                <span style={{ fontWeight: 700, color: '#047857' }}>Grand Total:</span>
                <span style={{ fontSize: '16px', fontWeight: 800, color: '#047857', fontFamily: 'var(--font-mono)' }}>฿{activeProp.grandTotal.toLocaleString()}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
